import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { createFeature, createReducer, on } from '@ngrx/store';
import {
  AccountDto,
  AccountSecurityDto,
  FeatureDetailDto,
  GroupDto,
  InvitationDto,
  PermissionDto,
} from './models/rbac.model';
import { RbacActions } from './rbac.actions';

// ─── Entity adapters ─────────────────────────────────────────────────────────

export const groupsAdapter = createEntityAdapter<GroupDto>({
  selectId: (g) => g.accountUuid,
});

export const accountsAdapter = createEntityAdapter<AccountDto>({
  selectId: (a) => a.accountUuid,
});

// ─── State ───────────────────────────────────────────────────────────────────

export interface RbacState {
  currentTenantUuid: string | null;

  groups: EntityState<GroupDto>;
  groupsLoading: boolean;
  groupsSaving: boolean;
  groupsError: string | null;
  groupsQuery: string;
  groupsFilters: Record<string, unknown[]>;

  accounts: EntityState<AccountDto>;
  accountsLoading: boolean;
  accountsSaving: boolean;
  accountsError: string | null;
  accountsQuery: string;
  accountsFilters: Record<string, unknown[]>;
  accountsTotalElements: number;
  accountsCurrentPage: number;
  accountsPageSize: number;

  featureDetails: readonly FeatureDetailDto[];
  featureDetailsLoading: boolean;

  permissions: readonly PermissionDto[];
  permissionsLoading: boolean;

  invitations: readonly InvitationDto[];
  invitationsLoading: boolean;
}

export const initialState: RbacState = {
  currentTenantUuid: null,

  groups: groupsAdapter.getInitialState(),
  groupsLoading: false,
  groupsSaving: false,
  groupsError: null,
  groupsQuery: '',
  groupsFilters: {},

  accounts: accountsAdapter.getInitialState(),
  accountsLoading: false,
  accountsSaving: false,
  accountsError: null,
  accountsQuery: '',
  accountsFilters: {},
  accountsTotalElements: 0,
  accountsCurrentPage: 0,
  accountsPageSize: 20,

  featureDetails: [],
  featureDetailsLoading: false,

  permissions: [],
  permissionsLoading: false,

  invitations: [],
  invitationsLoading: false,
};

// ─── Reducer ─────────────────────────────────────────────────────────────────

const reducer = createReducer(
  initialState,

  on(RbacActions.setTenant, (state, { tenantUuid }) => ({
    ...state,
    currentTenantUuid: tenantUuid,
  })),
  on(RbacActions.clear, () => initialState),

  // Groups
  on(RbacActions.loadGroups, (state) => ({ ...state, groupsLoading: true, groupsError: null })),
  on(RbacActions.loadGroupsSuccess, (state, { groups }) => ({
    ...state,
    groups: groupsAdapter.setAll([...groups], state.groups),
    groupsLoading: false,
  })),
  on(RbacActions.loadGroupsFailure, (state, { error }) => ({
    ...state,
    groupsLoading: false,
    groupsError: String(error),
  })),
  on(RbacActions.createGroup, (state) => ({ ...state, groupsSaving: true })),
  on(RbacActions.createGroupSuccess, (state, { group }) => ({
    ...state,
    groupsSaving: false,
    groups: groupsAdapter.addOne(group, state.groups),
  })),
  on(RbacActions.createGroupFailure, (state) => ({ ...state, groupsSaving: false })),
  on(RbacActions.updateGroup, (state) => ({ ...state, groupsSaving: true })),
  on(RbacActions.updateGroupSuccess, (state, { group }) => ({
    ...state,
    groupsSaving: false,
    groups: groupsAdapter.updateOne({ id: group.accountUuid, changes: group }, state.groups),
  })),
  on(RbacActions.updateGroupFailure, (state) => ({ ...state, groupsSaving: false })),
  on(RbacActions.deleteGroup, (state) => ({ ...state, groupsSaving: true })),
  on(RbacActions.deleteGroupSuccess, (state, { groupUuid }) => ({
    ...state,
    groupsSaving: false,
    groups: groupsAdapter.removeOne(groupUuid, state.groups),
  })),
  on(RbacActions.deleteGroupFailure, (state) => ({ ...state, groupsSaving: false })),
  on(RbacActions.setGroupsQuery, (state, { query }) => ({ ...state, groupsQuery: query })),
  on(RbacActions.setGroupsFilters, (state, { filters }) => ({ ...state, groupsFilters: filters })),

  // Accounts
  on(RbacActions.loadAccounts, (state, { page, size, search, filters }) => ({
    ...state,
    ...(search !== undefined && { search }),
    ...(filters !== undefined && { filters }),
    accountsLoading: true,
    accountsError: null,
    accountsCurrentPage: page ?? state.accountsCurrentPage,
    accountsPageSize: size ?? state.accountsPageSize,
  })),
  on(RbacActions.loadAccountsSuccess, (state, { accounts, totalElements, page }) => ({
    ...state,
    accounts: accountsAdapter.setAll([...accounts], state.accounts),
    accountsLoading: false,
    accountsTotalElements: totalElements,
    accountsCurrentPage: page,
  })),
  on(RbacActions.loadAccountsFailure, (state, { error }) => ({
    ...state,
    accountsLoading: false,
    accountsError: String(error),
  })),
  on(RbacActions.createAccount, (state) => ({ ...state, accountsSaving: true })),
  on(RbacActions.createAccountSuccess, (state, { account }) => ({
    ...state,
    accountsSaving: false,
    accounts: accountsAdapter.addOne(account, state.accounts),
  })),
  on(RbacActions.createAccountFailure, (state, { error: _ }) => ({
    ...state,
    accountsSaving: false,
  })),
  on(RbacActions.updateAccount, (state) => ({ ...state, accountsSaving: true })),
  on(RbacActions.updateAccountSuccess, (state, { account }) => ({
    ...state,
    accountsSaving: false,
    accounts: accountsAdapter.updateOne(
      { id: account.accountUuid, changes: account },
      state.accounts,
    ),
  })),
  on(RbacActions.updateAccountFailure, (state, { error: _ }) => ({
    ...state,
    accountsSaving: false,
  })),
  on(RbacActions.deleteAccountSuccess, (state, { accountUuid }) => ({
    ...state,
    accounts: accountsAdapter.removeOne(accountUuid, state.accounts),
  })),
  on(RbacActions.blockAccountSuccess, (state, { accountUuid }) => {
    const existing = state.accounts.entities[accountUuid];
    return {
      ...state,
      accounts: accountsAdapter.updateOne(
        {
          id: accountUuid,
          changes: {
            accountSecurity: {
              ...(existing?.accountSecurity as AccountSecurityDto),
              blocked: true,
            },
          },
        },
        state.accounts,
      ),
    };
  }),
  on(RbacActions.unblockAccountSuccess, (state, { accountUuid }) => {
    const existing = state.accounts.entities[accountUuid];
    return {
      ...state,
      accounts: accountsAdapter.updateOne(
        {
          id: accountUuid,
          changes: {
            accountSecurity: {
              ...(existing?.accountSecurity as AccountSecurityDto),
              blocked: false,
            },
          },
        },
        state.accounts,
      ),
    };
  }),
  on(RbacActions.setAccountsQuery, (state, { query }) => ({ ...state, accountsQuery: query })),
  on(RbacActions.setAccountsFilters, (state, { filters }) => ({
    ...state,
    accountsFilters: filters,
  })),

  // Feature Details
  on(RbacActions.loadFeatureDetails, (state) => ({ ...state, featureDetailsLoading: true })),
  on(RbacActions.loadFeatureDetailsSuccess, (state, { featureDetails }) => ({
    ...state,
    featureDetails,
    featureDetailsLoading: false,
  })),
  on(RbacActions.loadFeatureDetailsFailure, (state) => ({
    ...state,
    featureDetailsLoading: false,
  })),

  // Permissions
  on(RbacActions.loadPermissions, (state) => ({ ...state, permissionsLoading: true })),
  on(RbacActions.loadPermissionsSuccess, (state, { permissions }) => ({
    ...state,
    permissions,
    permissionsLoading: false,
  })),
  on(RbacActions.loadPermissionsFailure, (state) => ({ ...state, permissionsLoading: false })),
  on(RbacActions.grantPermissionSuccess, (state, { permission }) => ({
    ...state,
    permissions: [...state.permissions, permission],
  })),
  on(
    RbacActions.revokePermissionSuccess,
    (state, { accountUuid, featureDetailName, itemUuid }) => ({
      ...state,
      permissions: state.permissions.filter(
        (p) =>
          !(
            p.accountUuid === accountUuid &&
            p.featureDetailName === featureDetailName &&
            p.itemUuid === itemUuid
          ),
      ),
    }),
  ),

  // Invitations
  on(RbacActions.loadInvitations, (state) => ({ ...state, invitationsLoading: true })),
  on(RbacActions.loadInvitationsSuccess, (state, { invitations }) => ({
    ...state,
    invitations,
    invitationsLoading: false,
  })),
  on(RbacActions.loadInvitationsFailure, (state) => ({ ...state, invitationsLoading: false })),
  on(RbacActions.sendInvitationSuccess, (state, { invitation }) => ({
    ...state,
    invitations: [...state.invitations, invitation],
  })),
  on(RbacActions.revokeInvitationSuccess, (state, { invitationId }) => ({
    ...state,
    invitations: state.invitations.filter((i) => i.invitationId !== invitationId),
  })),
);

export const rbacFeature = createFeature({
  name: 'rbac',
  reducer,
});
